import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { POIModelPoiType, SettingModelSettingType } from 'api/models';
import { useGetSettingTypesByPOI } from 'api/queries/poi/poi';
import { Catalogue } from 'app/modules/wallforming/components/catalogue-container/Catalogue';
import { CatalogueArticlePreview } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticlePreview';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { AutomatedPlacementAction } from 'app/modules/wallforming/components/setting-container/automated-placement/AutomatedPlacementAction';
import { PlacementTypeHeader } from 'app/modules/wallforming/components/setting-container/automated-placement/PlacementTypeHeader';
import { CornerPlacementContainer } from 'app/modules/wallforming/components/setting-container/corner-placement/CornerPlacementContainer';
import { useGetAutomatedCornerPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useGetAutomatedCornerPlacement';
import { useHandleConnectorPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useHandleConnectorPlacement';
import { useHandlePanelPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useHandlePanelPlacement';
import { PanelPlacementSettings } from 'app/modules/wallforming/components/setting-container/panel-placement/PanelPlacementSettings';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const settingTypes = Object.values(SettingModelSettingType);
const previousPoiNavigationCodes = new Set(['ArrowLeft', 'KeyA']);
const nextPoiNavigationCodes = new Set(['ArrowRight', 'KeyD']);
const executionCodes = new Set(['Enter', 'NumpadEnter', 'KeyE']);

export const AutomatedPlacementContainer = () => {
    const {
        selectedPoiType,
        selectedSettingType,
        selectedSettingView,
        automatedPlacementTab,
        setAutomatedPlacementTab,
        setSelectedSettingType
    } = usePlacementStore(
        useShallow((state) => ({
            selectedPoiType: state.poiSelection.current.filterType,
            selectedSettingType: state.selectedSettingType,
            automatedPlacementTab: state.automatedPlacementTab,
            setAutomatedPlacementTab: state.setAutomatedPlacementTab,
            setSelectedSettingType: state.setSelectedSettingType,
            selectedSettingView: state.selectedSettingView
        }))
    );

    const [areCornerSolutionsLoading, setAreCornerSolutionsLoading] = useState<boolean>(false);
    const loadCornerSolutions = useGetAutomatedCornerPlacement({ setLoadingState: setAreCornerSolutionsLoading });
    const { handlePanelPlacementStart } = useHandlePanelPlacement();
    const { handleConnectorPlacementStart } = useHandleConnectorPlacement();

    const { data: availableSettingTypesResponse, isLoading: isLoadingSettingTypes } = useGetSettingTypesByPOI(
        selectedPoiType === POIFilterType.Corner ? POIModelPoiType.PlanarFace : POIModelPoiType.StraightWall,
        {
            query: {
                queryKey: ['automated-placement-setting-types', selectedPoiType],
                enabled: selectedPoiType !== undefined,
                staleTime: 24 * 60 * 60 * 1000
            }
        }
    );

    const availableSettingTypes = useMemo(() => {
        const data = availableSettingTypesResponse?.data ?? [];
        if (data.length === 0) {
            return settingTypes;
        }

        const availableSet = new Set(data);
        return settingTypes.filter((settingType) => availableSet.has(settingType));
    }, [availableSettingTypesResponse?.data]);

    const isAutomationViewActive = useMemo(
        () => selectedSettingView === SettingView.AUTOMATED_PLACEMENT,
        [selectedSettingView]
    );

    const isCornerPlacementActive = useMemo(
        () =>
            isAutomationViewActive &&
            selectedSettingType === SettingModelSettingType.Corners &&
            selectedPoiType === POIFilterType.Corner,
        [isAutomationViewActive, selectedSettingType, selectedPoiType]
    );

    const loadPlacementSolutions = useMemo(
        () => isCornerPlacementActive && automatedPlacementTab === PlacementTab.SOLUTIONS,
        [isCornerPlacementActive, automatedPlacementTab]
    );

    const loadAutomatedPlacement = useMemo(
        () =>
            isAutomationViewActive &&
            (selectedSettingType === SettingModelSettingType.Panels ||
                selectedSettingType === SettingModelSettingType.Connectors),
        [isAutomationViewActive, selectedSettingType]
    );

    useEffect(() => {
        if (isCornerPlacementActive) {
            setAutomatedPlacementTab(PlacementTab.SOLUTIONS);
        }
    }, [setAutomatedPlacementTab, isCornerPlacementActive]);

    useEffect(() => {
        if (availableSettingTypes.length === 0) {
            return;
        }

        if (!availableSettingTypes.includes(selectedSettingType)) {
            setSelectedSettingType(availableSettingTypes[0]);
        }
    }, [selectedSettingType, availableSettingTypes, setSelectedSettingType]);

    useEffect(() => {
        if (loadPlacementSolutions) {
            void loadCornerSolutions();
        }
    }, [loadPlacementSolutions, loadCornerSolutions]);

    const handleOnAutomatedPlacementStart = useCallback(() => {
        if (selectedSettingType === SettingModelSettingType.Panels) {
            handlePanelPlacementStart();
            return;
        }

        if (selectedSettingType === SettingModelSettingType.Connectors) {
            handleConnectorPlacementStart();
        }
    }, [handleConnectorPlacementStart, handlePanelPlacementStart, selectedSettingType]);

    const handleOnKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const state = usePlacementStore.getState();
            const { code } = event;

            if (isInputElement(event.target)) {
                return;
            }

            if (executionCodes.has(code) && loadAutomatedPlacement) {
                event.preventDefault();
                handleOnAutomatedPlacementStart();
                return;
            }

            const selectedSettingTypeIndex =
                Number.parseInt(code.match(/^(?:Digit|Numpad)([1-9])$/)?.[1] ?? '0', 10) - 1;
            if (
                selectedSettingTypeIndex >= 0 &&
                selectedSettingTypeIndex < availableSettingTypes.length &&
                isAutomationViewActive
            ) {
                event.preventDefault();
                state.setSelectedSettingType(availableSettingTypes[selectedSettingTypeIndex]);
                return;
            }

            const isPreviousPoiNavigation = previousPoiNavigationCodes.has(code);
            const isNextPoiNavigation = nextPoiNavigationCodes.has(code);
            if (isPreviousPoiNavigation || isNextPoiNavigation) {
                const poiIndexes = state.pois
                    .map((poi, index) => (poi.filterType === selectedPoiType ? index : -1))
                    .filter((index) => index >= 0);
                const currentPosition = poiIndexes.indexOf(state.poiSelection.current.index ?? -1);
                if (poiIndexes.length > 0 && currentPosition >= 0) {
                    event.preventDefault();
                    const nextPosition =
                        (currentPosition + (isPreviousPoiNavigation ? -1 : 1) + poiIndexes.length) % poiIndexes.length;
                    state.setPoiSelection(selectedPoiType, poiIndexes[nextPosition], PoiNavigationSource.PoiStepper);
                }
            }
        },
        [
            availableSettingTypes,
            handleOnAutomatedPlacementStart,
            isAutomationViewActive,
            loadAutomatedPlacement,
            selectedPoiType
        ]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleOnKeyDown);
        return () => window.removeEventListener('keydown', handleOnKeyDown);
    }, [handleOnKeyDown]);

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden border border-border-ultra-light">
            <PlacementTypeHeader
                actionNode={
                    <div className="flex items-center">
                        <AutomatedPlacementAction settingType={selectedSettingType} />
                    </div>
                }
                availableSettingTypes={availableSettingTypes}
                isLoading={isLoadingSettingTypes}
                selectedSettingType={selectedSettingType}
                onSelectSettingType={setSelectedSettingType}
            />

            <div className="m-2 flex min-h-0 flex-1 flex-col gap-2">
                <div className="flex min-h-0 max-h-1/3 flex-none flex-col overflow-hidden">
                    <div className="shrink-0">
                        <PanelPlacementSettings />
                    </div>
                </div>

                {selectedSettingType === SettingModelSettingType.Corners ? (
                    <CornerPlacementContainer areCornerSolutionsLoading={areCornerSolutionsLoading} />
                ) : (
                    <div className="grid min-h-0 h-full min-w-0 flex-1 grid-rows-[minmax(11rem,1.25fr)_minmax(7rem,0.75fr)] gap-2 overflow-hidden">
                        <div className="min-h-0 min-w-0 overflow-hidden" data-automated-catalogue>
                            <Catalogue settingType={selectedSettingType} showFilter={false} />
                        </div>
                        <div className="min-h-0 min-w-0 overflow-hidden">
                            <CatalogueArticlePreview />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
